// Single source of truth for the 6 Triguna Solutions services.
// Powers the homepage services grid, the navbar dropdown, the footer,
// the /services overview, and the dynamic /services/:slug detail template.
//
// `icon` values are lucide-react icon names, resolved in components.

export const SERVICES = [
  {
    slug: "generative-ai",
    name: "Generative AI Solutions",
    icon: "Sparkles",
    tagline: "AI Chatbots, Agents, RAG & LLM integration for the enterprise",
    image:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1200&q=80",
    features: [
      "AI Chatbots",
      "AI Agents",
      "RAG Systems",
      "LLM Integration",
      "Enterprise AI Solutions",
    ],
    seoTitle: "Generative AI Development Company USA | Triguna Solutions",
    seoDescription:
      "Build AI chatbots, autonomous AI agents, RAG systems, and enterprise LLM integrations with Triguna Solutions — a US-based Generative AI development company.",
    hero: {
      headline: "Generative AI Solutions That Drive Real Outcomes",
      subheadline:
        "From intelligent chatbots and autonomous agents to retrieval-augmented generation and enterprise LLM integration — we turn frontier AI into production-grade systems.",
    },
    overview:
      "We help organizations move beyond AI experiments to dependable, secure, and scalable generative AI in production. Our engineers and data scientists design systems that reason over your data, automate workflows, and create measurable business value.",
    benefits: [
      { title: "Faster Time to Value", description: "Reusable AI accelerators and proven patterns get you to production in weeks, not quarters." },
      { title: "Grounded & Accurate", description: "RAG pipelines connect models to your trusted data, reducing hallucination and boosting accuracy." },
      { title: "Secure by Design", description: "Private deployments, data governance, and access controls keep sensitive information protected." },
      { title: "Cost-Optimized", description: "Smart model routing and caching balance quality and spend across your AI workloads." },
    ],
    useCases: [
      "Customer support assistants & copilots",
      "Knowledge base & document Q&A (RAG)",
      "Autonomous agents for back-office automation",
      "Content generation & summarization",
      "AI-powered search & recommendations",
      "Code generation & developer tooling",
    ],
    technologies: ["OpenAI", "Claude", "LangChain", "LlamaIndex", "Vector Databases", "Python", "FastAPI"],
    process: ["Discovery", "Use-case Design", "Data & RAG Pipeline", "Model Integration", "Evaluation", "Deployment", "Optimization"],
  },
  {
    slug: "mobile-app-development",
    name: "Mobile App Development",
    icon: "Smartphone",
    tagline: "iOS, Android & cross-platform apps users love",
    image:
      "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1200&q=80",
    features: ["iOS Apps", "Android Apps", "Cross-platform Apps", "React Native", "Flutter"],
    seoTitle: "Mobile App Development Company | iOS, Android & Cross-Platform",
    seoDescription:
      "Triguna Solutions builds high-performance iOS, Android, and cross-platform mobile apps using React Native and Flutter for startups and enterprises across the USA.",
    hero: {
      headline: "Mobile Apps Engineered for Performance & Scale",
      subheadline:
        "Native and cross-platform mobile experiences — designed for delight, built for reliability, and ready for millions of users.",
    },
    overview:
      "We design and build mobile products end to end: strategy, UX, engineering, and launch. Whether you need a native iOS/Android app or a single cross-platform codebase, we deliver fast, secure, and maintainable applications.",
    benefits: [
      { title: "One Codebase, Every Platform", description: "Cross-platform frameworks cut cost and time-to-market while keeping a native feel." },
      { title: "App-Store Ready", description: "We handle store guidelines, performance budgets, and release pipelines end to end." },
      { title: "Offline-First & Fast", description: "Resilient architectures that work in poor connectivity and feel instant to users." },
      { title: "Secure & Compliant", description: "Encryption, secure storage, and compliance-aware design built into every app." },
    ],
    useCases: [
      "Consumer & lifestyle apps",
      "Enterprise field & workforce apps",
      "On-demand & marketplace platforms",
      "Healthcare & fintech mobile apps",
      "IoT & connected-device companions",
      "Mobile commerce experiences",
    ],
    technologies: ["React Native", "Flutter", "Swift", "Kotlin", "Firebase", "GraphQL"],
    process: ["Discovery", "UX Strategy", "Design", "Development", "Testing", "Store Launch", "Support"],
  },
  {
    slug: "web-development",
    name: "Web Development",
    icon: "Globe",
    tagline: "Enterprise apps, SaaS platforms & custom portals",
    image:
      "https://images.unsplash.com/photo-1547658719-da2b51169166?auto=format&fit=crop&w=1200&q=80",
    features: ["Enterprise Applications", "SaaS Platforms", "E-commerce Solutions", "Custom Web Portals"],
    seoTitle: "Web Development Services | Enterprise & SaaS Platforms",
    seoDescription:
      "Triguna Solutions delivers enterprise web applications, SaaS platforms, e-commerce, and custom portals built with modern, scalable web technologies.",
    hero: {
      headline: "Web Applications Built for Enterprise Scale",
      subheadline:
        "From SaaS platforms to complex enterprise portals — we engineer fast, accessible, and secure web products that grow with your business.",
    },
    overview:
      "We build modern web applications that combine exceptional user experience with robust, scalable architecture. Our teams ship performant SaaS platforms, e-commerce, and bespoke enterprise systems on proven web technologies.",
    benefits: [
      { title: "Built to Scale", description: "Modular, cloud-native architectures that handle growth without rewrites." },
      { title: "Lightning Performance", description: "Optimized rendering, caching, and Core Web Vitals for speed and SEO." },
      { title: "Accessible & Responsive", description: "WCAG-aware, mobile-first interfaces that work everywhere." },
      { title: "Secure & Reliable", description: "Hardened APIs, automated testing, and resilient deployments." },
    ],
    useCases: [
      "Multi-tenant SaaS platforms",
      "Enterprise dashboards & admin portals",
      "Customer & partner self-service portals",
      "E-commerce & marketplace storefronts",
      "Internal workflow & operations tools",
      "Headless CMS & content platforms",
    ],
    technologies: ["React", "Next.js", "Angular", "Vue", "Node.js", "TypeScript", "PostgreSQL"],
    process: ["Discovery", "Architecture", "Design", "Development", "Testing", "Deployment", "Support"],
  },
  {
    slug: "cloud-engineering",
    name: "Cloud Engineering",
    icon: "Cloud",
    tagline: "AWS, Azure, GCP, DevOps & Kubernetes",
    image:
      "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1200&q=80",
    features: ["AWS", "Azure", "Google Cloud", "DevOps", "Kubernetes"],
    seoTitle: "Cloud Engineering Services | AWS, Azure, GCP & DevOps",
    seoDescription:
      "Triguna Solutions provides cloud engineering, migration, DevOps, and Kubernetes services on AWS, Azure, and Google Cloud for resilient, scalable infrastructure.",
    hero: {
      headline: "Cloud Engineering & DevOps That Scale With You",
      subheadline:
        "Architect, migrate, and operate cloud-native infrastructure on AWS, Azure, and Google Cloud — with automation, security, and cost control at the core.",
    },
    overview:
      "We help you adopt the cloud with confidence — designing resilient architectures, automating delivery with DevOps and Kubernetes, and migrating workloads with minimal disruption while keeping costs under control.",
    benefits: [
      { title: "Resilient Architecture", description: "Highly available, fault-tolerant systems engineered for uptime." },
      { title: "Automated Delivery", description: "CI/CD, infrastructure-as-code, and GitOps for fast, safe releases." },
      { title: "Cost Optimization", description: "Right-sizing, autoscaling, and FinOps practices to control spend." },
      { title: "Security & Compliance", description: "Cloud security best practices and compliance-ready guardrails." },
    ],
    useCases: [
      "Cloud migration & modernization",
      "Kubernetes & container platforms",
      "CI/CD & DevOps automation",
      "Infrastructure-as-Code (Terraform)",
      "Observability & SRE practices",
      "Multi-cloud & hybrid architectures",
    ],
    technologies: ["AWS", "Azure", "Google Cloud", "Kubernetes", "Docker", "Terraform", "GitHub Actions"],
    process: ["Assessment", "Cloud Strategy", "Architecture", "Migration", "Automation", "Hardening", "Operations"],
  },
  {
    slug: "data-engineering",
    name: "Data Engineering",
    icon: "Database",
    tagline: "Pipelines, data lakes, ETL & real-time analytics",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80",
    features: ["Data Pipelines", "Data Lakes", "ETL Solutions", "Real-time Analytics"],
    seoTitle: "Data Engineering Company | Pipelines, Data Lakes & ETL",
    seoDescription:
      "Triguna Solutions builds robust data pipelines, data lakes, ETL workflows, and real-time analytics platforms to turn raw data into business intelligence.",
    hero: {
      headline: "Data Engineering for a Data-Driven Enterprise",
      subheadline:
        "Reliable pipelines, modern data lakes, and real-time analytics that turn raw data into trusted, actionable intelligence.",
    },
    overview:
      "We build the data foundation modern businesses run on — ingesting, transforming, and serving data at scale. From batch ETL to streaming analytics, we make your data reliable, governed, and ready for AI.",
    benefits: [
      { title: "Trusted Data", description: "Quality checks, lineage, and governance you can rely on for decisions." },
      { title: "Real-Time Insight", description: "Streaming pipelines deliver fresh data when and where it's needed." },
      { title: "AI-Ready Foundations", description: "Well-modeled data lakes and warehouses that power analytics and ML." },
      { title: "Scalable & Efficient", description: "Cost-aware architectures that scale from gigabytes to petabytes." },
    ],
    useCases: [
      "Batch & streaming data pipelines",
      "Data lake & lakehouse architecture",
      "ETL / ELT modernization",
      "Real-time analytics & dashboards",
      "Data warehousing & modeling",
      "ML feature stores & data for AI",
    ],
    technologies: ["Snowflake", "Databricks", "Apache Spark", "Kafka", "Airflow", "dbt", "Python"],
    process: ["Discovery", "Data Strategy", "Modeling", "Pipeline Build", "Quality & Testing", "Deployment", "Monitoring"],
  },
  {
    slug: "product-engineering",
    name: "Product Engineering",
    icon: "Rocket",
    tagline: "MVPs, modernization, APIs & platform engineering",
    image:
      "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80",
    features: ["MVP Development", "Product Modernization", "API Development", "Platform Engineering"],
    seoTitle: "Product Engineering Services | MVP, Modernization & APIs",
    seoDescription:
      "Triguna Solutions offers end-to-end product engineering — MVP development, product modernization, API development, and platform engineering for scaling products.",
    hero: {
      headline: "Product Engineering From Idea to Scale",
      subheadline:
        "We partner with you across the product lifecycle — validating MVPs, modernizing legacy products, and engineering platforms built to scale.",
    },
    overview:
      "We act as your product engineering partner — bringing strategy, design, and full-stack engineering together to build, modernize, and scale software products that customers love and businesses depend on.",
    benefits: [
      { title: "Validate Fast", description: "Lean MVPs that test the market and de-risk investment quickly." },
      { title: "Modernize Confidently", description: "Incremental modernization that revitalizes products without big-bang risk." },
      { title: "API-First", description: "Well-designed APIs and platforms that unlock integrations and ecosystems." },
      { title: "Built to Last", description: "Maintainable, well-tested codebases that scale with your roadmap." },
    ],
    useCases: [
      "MVP & rapid prototyping",
      "Legacy product modernization",
      "API & integration platforms",
      "Platform & developer-experience engineering",
      "SaaS product scaling",
      "Dedicated product engineering teams",
    ],
    technologies: ["React", "Node.js", "Python", "Java", "GraphQL", "Docker", "Kubernetes"],
    process: ["Discovery", "Product Strategy", "Design", "Development", "Testing", "Launch", "Iterate"],
  },
];

// Lookup helper used by the dynamic service-detail route.
export const getServiceBySlug = (slug) => SERVICES.find((s) => s.slug === slug);
